import { queryString } from '@toggle/helpers/src/utils/strings/strings';
import React, { ReactElement, useMemo } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouteObject,
  RouterProvider,
} from 'react-router-dom';

import { ProtectedView } from '~/components/protected-view/ProtectedView';
import { Root } from '~/Root';
import { useAuthStore } from '~/stores/use-auth/store/authStore';
import {
  FeatureFlagsKeys,
  Flags,
} from '~/stores/use-feature-flags/feature-flags.types';
import { useFeatureFlags } from '~/stores/use-feature-flags/useFeatureFlags';

import { appPaths, authPaths, queryKeys } from './app-paths';
import { authRoutes, commonRoutes, featureRoutes, routes } from './AppRoutes';
import { Redirect } from './Redirect';

export type AppRoute = Omit<RouteObject, 'element'> & {
  element: ReactElement;
  crumb?: { isSidebar?: boolean; labelKey?: string; getLabel?: any };
};
export type FeatureRoutes = {
  [key in FeatureFlagsKeys]?: AppRoute;
};

export const filterFeatureRoutes = (
  featureRoutes: FeatureRoutes,
  features?: Flags
) => {
  if (!features) {
    return [];
  }

  return Object.entries(features).reduce<AppRoute[]>(
    (routes, [featureKey, value]) => {
      const route = featureRoutes[featureKey as FeatureFlagsKeys];
      if (value && route) {
        return [...routes, route];
      }

      return routes;
    },
    []
  );
};

export const Routes = () => {
  const isAuthenticated = useAuthStore(state => !!state.token);
  const { flags } = useFeatureFlags();
  const memoizedRoutes = useMemo(() => {
    const appRoutes: AppRoute[] = [
      ...commonRoutes,
      ...filterFeatureRoutes(featureRoutes, flags),
      ...(isAuthenticated ? routes : authRoutes),
    ];
    return appRoutes.map(routeConfig => {
      return (
        <Route
          path={routeConfig.path}
          element={
            <ProtectedView
              isAuthenticated={isAuthenticated}
              config={routeConfig}
            />
          }
          key={routeConfig.path}
        />
      );
    });
  }, [isAuthenticated, flags]);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="" element={<Root />}>
        <Route
          path={'/settings'}
          element={<Navigate to={appPaths.settings.account} replace />}
        />

        <Route
          path={'/promo/:id'}
          element={
            <Redirect
              from="/promo/:id"
              to={
                isAuthenticated
                  ? `${appPaths.settings.subscription}?promo=:id`
                  : `${authPaths.signUp}?promo=:id`
              }
            />
          }
        />

        <Route
          path="/sign-in"
          element={<Navigate to={authPaths.login} replace />}
        />
        {memoizedRoutes}
        {!isAuthenticated && (
          <Route
            path="/*"
            element={
              <Navigate
                to={
                  authPaths.login +
                  queryString({
                    [queryKeys.redirect]: `${location.pathname}${location.search}`,
                  })
                }
                replace
              />
            }
          />
        )}
      </Route>
    )
  );

  return <RouterProvider router={router} />;
};
